/**
 * FormSubmitPage
 *
 * Dynamic form renderer that builds a form from field definitions.
 * Uses React Hook Form for state management and Zod for validation.
 *
 * Requirements: Task 12 - Forms Pages
 */

import { useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Helmet } from 'react-helmet-async';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  useToast,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@bloomcode/ui';
import { ArrowLeft, ClipboardList, CheckCircle, RefreshCw } from '@bloomcode/icons';
import { useListPublicFormsQuery } from '@bloomcode/graphql';

import { useSubmitFormResponse } from '@/hooks/useForms';
import { DynamicFormField, type FormFieldDefinition } from '@/components/forms';

/**
 * Builds a Zod schema dynamically from form field definitions
 */
function buildZodSchema(fields: FormFieldDefinition[]) {
  const shape: Record<string, z.ZodTypeAny> = {};

  for (const field of fields) {
    let fieldSchema: z.ZodTypeAny;

    switch (field.type) {
      case 'email':
        fieldSchema = z.string().email('Please enter a valid email address');
        break;
      case 'number':
        fieldSchema = z.number({ invalid_type_error: `${field.label} must be a number` });
        break;
      case 'checkbox':
        fieldSchema = z.boolean();
        break;
      default:
        fieldSchema = z.string();
    }

    if (field.required) {
      if (field.type === 'checkbox') {
        fieldSchema = (fieldSchema as z.ZodBoolean).refine((val) => val === true, {
          message: `${field.label} is required`,
        });
      } else if (field.type === 'number') {
        // number is already required by default with z.number()
      } else {
        fieldSchema = (fieldSchema as z.ZodString).min(1, `${field.label} is required`);
      }
    } else {
      if (field.type !== 'checkbox' && field.type !== 'number') {
        fieldSchema = (fieldSchema as z.ZodString).optional().or(z.literal(''));
      }
    }

    shape[field.name] = fieldSchema;
  }

  return z.object(shape);
}

export default function FormSubmitPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { submitForm, isSubmitting } = useSubmitFormResponse();

  // Fetch the specific form by listing all and finding by ID
  const { data, loading, error } = useListPublicFormsQuery({
    variables: { pagination: { page: 1, limit: 100 } },
    fetchPolicy: 'cache-first',
  });

  const form = useMemo(() => data?.listPublicForms?.data?.find((f) => f.id === id), [data, id]);

  // Parse field definitions from JSON
  const fields: FormFieldDefinition[] = useMemo(() => {
    if (!form?.fields) return [];
    try {
      const parsed = typeof form.fields === 'string' ? JSON.parse(form.fields) : form.fields;
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [form?.fields]);

  // Build dynamic Zod schema
  const schema = useMemo(() => buildZodSchema(fields), [fields]);

  // Build default values
  const defaultValues = useMemo(() => {
    const values: Record<string, unknown> = {};
    for (const field of fields) {
      if (field.type === 'checkbox') {
        values[field.name] = false;
      } else if (field.type === 'number') {
        values[field.name] = undefined;
      } else {
        values[field.name] = '';
      }
    }
    return values;
  }, [fields]);

  const formMethods = useForm<Record<string, unknown>>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const onSubmit = async (values: Record<string, unknown>) => {
    if (!id) return;

    const result = await submitForm({
      formId: id,
      data: values,
      source: 'portal',
    });

    if (result.success) {
      toast({ title: 'Form submitted successfully', variant: 'default' });
    } else {
      toast({ title: result.message ?? 'Failed to submit form', variant: 'destructive' });
    }
  };

  // Loading state
  if (loading) {
    return (
      <>
        <Helmet>
          <title>Loading Form | Bloomcode CRM Portal</title>
        </Helmet>
        <div className="container mx-auto p-6">
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardContent className="p-6">
                <div className="animate-pulse space-y-4">
                  <div className="h-6 w-48 rounded bg-muted" />
                  <div className="h-4 w-full rounded bg-muted" />
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="space-y-2">
                        <div className="h-4 w-24 rounded bg-muted" />
                        <div className="h-10 w-full rounded bg-muted" />
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </>
    );
  }

  // Error state
  if (error) {
    return (
      <>
        <Helmet>
          <title>Error | Bloomcode CRM Portal</title>
        </Helmet>
        <div className="container mx-auto p-6">
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <p className="text-destructive mb-2">Failed to load form</p>
                <p className="text-sm text-muted-foreground mb-4">{error.message}</p>
                <Button variant="outline" onClick={() => navigate('/forms')}>
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Forms
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </>
    );
  }

  // Form not found
  if (!form) {
    return (
      <>
        <Helmet>
          <title>Form Not Found | Bloomcode CRM Portal</title>
        </Helmet>
        <div className="container mx-auto p-6">
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <ClipboardList className="h-12 w-12 text-muted-foreground/40 mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">Form Not Found</h3>
                <p className="text-muted-foreground mb-4">
                  This form may have been removed or is no longer available.
                </p>
                <Button variant="outline" onClick={() => navigate('/forms')}>
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Forms
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </>
    );
  }

  // Success state after submission
  if (formMethods.formState.isSubmitSuccessful) {
    return (
      <>
        <Helmet>
          <title>{form.name} — Submitted | Bloomcode CRM Portal</title>
        </Helmet>
        <div className="container mx-auto p-6">
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <CheckCircle className="h-12 w-12 text-primary mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">Form Submitted</h3>
                <p className="text-muted-foreground mb-6">
                  Your response to &ldquo;{form.name}&rdquo; has been submitted successfully.
                </p>
                <div className="flex items-center gap-3">
                  <Button variant="outline" onClick={() => navigate('/forms')}>
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to Forms
                  </Button>
                  <Button variant="outline" onClick={() => formMethods.reset()}>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Submit Another
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{form.name} | Bloomcode CRM Portal</title>
      </Helmet>

      <div className="container mx-auto p-6">
        <div className="max-w-2xl mx-auto">
          {/* Breadcrumb */}
          <Breadcrumb className="mb-4">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">Home</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/dashboard">Dashboard</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/forms">Forms</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Submit Form</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <Card>
            <CardHeader>
              <CardTitle>{form.name}</CardTitle>
              {form.description && (
                <p className="text-sm text-muted-foreground mt-1">{form.description}</p>
              )}
            </CardHeader>
            <CardContent>
              {fields.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  This form has no fields configured.
                </p>
              ) : (
                <form onSubmit={formMethods.handleSubmit(onSubmit)} className="space-y-5">
                  {fields.map((field) => (
                    <DynamicFormField
                      key={field.name}
                      field={field}
                      register={formMethods.register}
                      errors={formMethods.formState.errors}
                    />
                  ))}

                  <div className="flex justify-end gap-3 pt-4 border-t border-border">
                    <Button type="button" variant="outline" onClick={() => navigate('/forms')}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={isSubmitting}>
                      {isSubmitting ? 'Submitting...' : 'Submit'}
                    </Button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
